/**
 * @fileoverview Wrapper reactivo para FastAnswersModal que se suscribe al sharingInformationService
 * @module RxJFastAnswersModal
 * @category Component - Wrapper
 * @since 1.0.0
 * @version 1.0.0
 */
import React, {
  useEffect,
  useState,
  useRef,
  memo
} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import { sharingInformationService } from '../../../services/sharingInformationService';
import FastAnswersMC from './FastAnswersMC';
import useSelectedConversation from '../../hooks/shared/useSelectedConversation';
import IconMC from "../../components/IconMC";

/**
 * Wrapper reactivo para la selección de respuestas rápidas que se integra con sharingInformationService.
 *
 * @component RxJFastAnswersModal
 * @category Component - Wrapper
 */
const RxJFastAnswersModal = memo(({ className }) => {
  const { t } = useTranslation('InboxMC');
  const [showModal, setShowModal] = useState(false);
  const conversation = useSelectedConversation();
  const { customer } = useSelector((state) => state.inboxRightReducer);

  // Suscribirse al sharingInformationService
  useEffect(() => {
    const subscription$ = sharingInformationService.getSubject();

    const myObservable = subscription$.subscribe((data) => {
      // Abrir modal de selección de respuestas rápidas
      if ('openFastAnswersSelection' in data) {
        setShowModal(data.openFastAnswersSelection);
      }

      // Cerrar modal
      if ('closeFastAnswersSelection' in data && data.closeFastAnswersSelection) {
        setShowModal(false);
      }
    });

    // Cleanup: desuscribirse al desmontar componente
    return () => {
      myObservable.unsubscribe();
    };
  }, []);

  useEffect(() => {
    sharingInformationService.setSubject({
      fastAnswersSelectionModalOpen: showModal
    });
    sharingInformationService.setSubject({});
    if (showModal) {
      sharingInformationService.setSubject({
        closePaymentLinksForPdf: true,
        closePdfModal: true,
        closeProductSelection: true
      });
      sharingInformationService.setSubject({});
    }
  }, [showModal]);

  /**
   * Función para cerrar el modal.
   */
  const onClose = () => {
    setShowModal(false);
  };

  // No renderizar si el modal está cerrado
  if (!showModal) {
    return null;
  }

  return (
    <div className={className}>
      <div className="tw-flex tw-items-center tw-justify-between tw-p-4 tw-border-b tw-border-gray-200">
        <span className="tw-font-semibold tw-text-gray-950 tw-text-sm">{t('chat.fast')}</span>
        <IconMC
          name="close-outline"
          onClick={onClose}
          className="tw-cursor-pointer tw-text-gray-500 hover:tw-text-gray-700"
        />
      </div>
      <div className="tw-flex-1 tw-overflow-y-auto tw-p-4">
        <FastAnswersMC
          currentCustomer={customer}
          platform={conversation?.platform}
          closeModal={onClose}
        />
      </div>
    </div>
  );
});

RxJFastAnswersModal.displayName = 'RxJFastAnswersModal';

export default RxJFastAnswersModal;
